import React, { useEffect, useState } from "react";
import { Modal, Button, Row, Col, Container, Form, Spinner } from "react-bootstrap";
import { Formik, Field, FieldArray } from "formik";
import * as Yup from "yup";
import { useAppSelector, useAppDispatch } from "../../../../store/hooks";
import {
	updateRoleAsync,
	getAllPermissionsAsync,
	getAllRolesAsync,
	getARoleAsync,
	setRoleId,
} from "../../../../slices/settings/settingsSlice";
import { RootState } from "../../../../store/store";
import { toast } from "react-toastify";

interface EditRoleValues {
	role_name: string;
	id: string;
	company_id: string;
	permissions: string[];
}

interface EditRoleProps {
	closeModal: () => void;
	show: boolean;
	roleId: string;
	onEditSuccess: () => void;
}

const EditRole: React.FC<EditRoleProps> = ({
	closeModal,
	show,
	roleId,
	onEditSuccess,
}) => {
	const dispatch = useAppDispatch();
	const companyId = useAppSelector(
		(state: RootState) => state.auth.user?.company_id || "",
	);
	const roleData = useAppSelector((state: any) => state.settings.roleData);

	const permissions =
		useAppSelector((state: RootState) => state.settings.permissions) || [];
	const [loading, setLoading] = useState(true);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [pageNumber, setPage] = useState(1);

	useEffect(() => {
		dispatch(getAllPermissionsAsync());
	}, [dispatch]);

	useEffect(() => {
		if (roleId) {
			dispatch(setRoleId(roleId));
			console.log("Fetching role with ID:", roleId);
			dispatch(getARoleAsync({ companyId, id: roleId })).then(() =>
				setLoading(false),
			);
		} else {
			console.log("roleId is not set");
		}
	}, [dispatch, companyId, roleId]);

	if (loading) {
		return null;
	}

	const getPermissionsByCategory = (category: string) => {
		return permissions.filter((permission) =>
			permission.permission_name.includes(category),
		);
	};
	console.log("Permissions:", permissions);
	console.log(
		"Permission:",
		roleData?.permissions.map((permission: { id: any }) =>
			String(permission.id),
		),
	);
	console.log("Role:", roleData);
	return (
		<Container fluid className="">
			<Modal
				centered
				size="lg"
				show={show}
				onHide={closeModal}
				style={{ maxHeight: "100vh", overflowY: "auto" }}
			>
				<div className="px-5">
					<Formik
						enableReinitialize
						initialValues={{
							company_id: companyId || "",
							id: roleData?.role.id || "",
							role_name: roleData?.role.role_name || "",
							permissions:
								roleData?.permissions.map((permission: { id: any }) =>
									String(permission.id),
								) || [],
						}}
						validationSchema={Yup.object().shape({
							company_id: Yup.string(),
							id: Yup.string(),
							role_name: Yup.string(),
							permissions: Yup.array().of(Yup.string()),
						})}
						onSubmit={(values: EditRoleValues, { setSubmitting }) => {
							console.log("Submitting values:", values);
							dispatch(
								updateRoleAsync({
									updateRoleData: {
										...values,
										company_id: companyId || "",
										permissions: values.permissions.map(String),
									},
									id: roleId || "",
								}),
							).unwrap()
								.then((response) => {
									toast.success("Role updated successfully");
									dispatch(getAllRolesAsync({ companyId, page: pageNumber }));
									onEditSuccess();
									closeModal();
								})
								.catch((error) => {
									const errorMessage = Object.values(error.message).flat().join(" ");
									toast.error(errorMessage);
								})
								.finally(() => {
									setSubmitting(false);
								});
						}}
					>
						{({
							errors,
							touched,
							handleSubmit,
							isSubmitting,
							values,
							handleChange,
						}) => (
							<Form onSubmit={handleSubmit}>
								<Modal.Header closeButton className="text-start mt-2">
									<h4 className="confirm-header">Edit a role</h4>
								</Modal.Header>
								<Modal.Body>
									<Row>
										<Col md={6} className="mt-2">
											<label className="mt-2">
												<p>
													Role <span className="text-danger">*</span>
												</p>
											</label>
											<Field
												type="text"
												name="role_name"
												placeholder="Enter role name"
												className={`fluent-input ${touched.role_name && errors.role_name
													? "is-invalid"
													: ""
													}`}
											/>
										</Col>
									</Row>
									<Row>
										<Col md={12} className="mt-3">
											<p className="mt-2 fw-bold">Permissions</p>
											<FieldArray
												name="permissions"
												render={(arrayHelpers) => (
													<div>
														<Row>
															<Col md={2} className="mt-2">
																<p className="fw-bold">Request</p>
															</Col>
															<Col className="mt-1 text-center">
																<span>Create</span>
																<Form.Check
																	type="checkbox"
																	name="permissions"
																	value="20"
																	checked={values.permissions.includes("20")}
																	onChange={(e) => {
																		if (e.target.checked) {
																			arrayHelpers.push("20");
																		} else {
																			arrayHelpers.remove(values.permissions.indexOf("20"));
																		}
																	}}
																/>
															</Col>
															<Col className="mt-1 text-center">
																<span>Edit</span>
																<Form.Check
																	type="checkbox"
																	name="permissions"
																	value="21"
																	checked={values.permissions.includes("21")}
																	onChange={(e) => {
																		if (e.target.checked) {
																			arrayHelpers.push("21");
																		} else {
																			arrayHelpers.remove(values.permissions.indexOf("21"));
																		}
																	}}
																/>
															</Col>
															<Col className="mt-1 text-center">
																<span>View</span>
																<Form.Check
																	type="checkbox"
																	name="permissions"
																	value="22"
																	checked={values.permissions.includes("22")}
																	onChange={(e) => {
																		if (e.target.checked) {
																			arrayHelpers.push("22");
																		} else {
																			arrayHelpers.remove(values.permissions.indexOf("22"));
																		}
																	}}
																/>
															</Col>
															<Col className="mt-1 text-center">
																<span>Delete</span>
																<Form.Check
																	type="checkbox"
																	name="permissions"
																	value="23"
																	checked={values.permissions.includes("23")}
																	onChange={(e) => {
																		if (e.target.checked) {
																			arrayHelpers.push("23");
																		} else {
																			arrayHelpers.remove(values.permissions.indexOf("23"));
																		}
																	}}
																/>
															</Col>
															<Col className="mt-1 text-center">
																<span>Approve/Reject</span>
																<Form.Check
																	type="checkbox"
																	name="permissions"
																	value="24"
																	checked={values.permissions.includes("24")}
																	onChange={(e) => {
																		if (e.target.checked) {
																			if (values.permissions.includes("25")) {
																				arrayHelpers.remove(values.permissions.indexOf("25"));
																			}
																			arrayHelpers.push("24");
																		} else {
																			arrayHelpers.remove(values.permissions.indexOf("24"));
																		}
																	}}
																/>
															</Col>
															<Col className="mt-1 text-center">
																<span>Review/Reject</span>
																<Form.Check
																	type="checkbox"
																	name="permissions"
																	value="25"
																	checked={values.permissions.includes("25")}
																	onChange={(e) => {
																		if (e.target.checked) {
																			if (values.permissions.includes("24")) {
																				arrayHelpers.remove(values.permissions.indexOf("24"));
																			}
																			arrayHelpers.push("25");
																		} else {
																			arrayHelpers.remove(values.permissions.indexOf("25"));
																		}
																	}}
																/>
															</Col>
														</Row>


														{/* Budget Permissions */}
														<Row className="mt-2">
															<Col md={2} className="mt-2">
																<p className="fw-bold">Budget</p>
															</Col>
															{getPermissionsByCategory("budget").map(
																(permission) => (
																	<Col
																		md={2}
																		className="mt-1 text-center"
																		key={permission.id}
																	>
																		<span>
																			{permission.permission_name.split("-")[0]}
																		</span>
																		<Form.Check
																			type="checkbox"
																			name="permissions"
																			value={String(permission.id)}
																			checked={values.permissions.includes(
																				String(permission.id),
																			)}
																			onChange={(e) => {
																				if (e.target.checked) {
																					arrayHelpers.push(
																						String(permission.id),
																					);
																				} else {
																					const idx =
																						values.permissions.indexOf(
																							String(permission.id),
																						);
																					arrayHelpers.remove(idx);
																				}
																			}}
																		/>
																	</Col>
																),
															)}
														</Row>
														{/* Scheduling Permissions */}
														<Row className="mt-2">
															<Col md={2} className="mt-2">
																<p className="fw-bold">Scheduling</p>
															</Col>
															{getPermissionsByCategory("schedule").map(
																(permission) => (
																	<Col
																		md={2}
																		className="mt-1 text-center"
																		key={permission.id}
																	>
																		<span>
																			{permission.permission_name.split("-")[0]}
																		</span>
																		<Form.Check
																			type="checkbox"
																			name="permissions"
																			value={String(permission.id)}
																			checked={values.permissions.includes(
																				String(permission.id),
																			)}
																			onChange={(e) => {
																				if (e.target.checked) {
																					arrayHelpers.push(
																						String(permission.id),
																					);
																				} else {
																					const idx =
																						values.permissions.indexOf(
																							String(permission.id),
																						);
																					arrayHelpers.remove(idx);
																				}
																			}}
																		/>
																	</Col>
																),
															)}
														</Row>
														{/* Report Permissions */}
														<Row className="mt-2">
															<Col md={2} className="mt-2">
																<p className="fw-bold">Report</p>
															</Col>
															{getPermissionsByCategory("report").map(
																(permission) => (
																	<Col
																		md={2}
																		className="mt-1 text-center"
																		key={permission.id}
																	>
																		<span>
																			{permission.permission_name.split("-")[0]}
																		</span>
																		<Form.Check
																			type="checkbox"
																			name="permissions"
																			value={String(permission.id)}
																			checked={values.permissions.includes(
																				String(permission.id),
																			)}
																			onChange={(e) => {
																				if (e.target.checked) {
																					arrayHelpers.push(
																						String(permission.id),
																					);
																				} else {
																					const idx =
																						values.permissions.indexOf(
																							String(permission.id),
																						);
																					arrayHelpers.remove(idx);
																				}
																			}}
																		/>
																	</Col>
																),
															)}
														</Row>
														{/* Inventory Permissions */}
														<Row className="mt-2">
															<Col md={2} className="mt-2">
																<p className="fw-bold">Inventory</p>
															</Col>
															{getPermissionsByCategory("inventory").map(
																(permission) => (
																	<Col
																		md={2}
																		className="mt-1 text-center"
																		key={permission.id}
																	>
																		<span>
																			{permission.permission_name.split("-")[0]}
																		</span>
																		<Form.Check
																			type="checkbox"
																			name="permissions"
																			value={String(permission.id)}
																			checked={values.permissions.includes(
																				String(permission.id),
																			)}
																			onChange={(e) => {
																				if (e.target.checked) {
																					arrayHelpers.push(
																						String(permission.id),
																					);
																				} else {
																					const idx =
																						values.permissions.indexOf(
																							String(permission.id),
																						);
																					arrayHelpers.remove(idx);
																				}
																			}}
																		/>
																	</Col>
																),
															)}
														</Row>
														{/* Accounts Permissions */}
														<Row className="mt-2">
															<Col md={2} className="mt-2">
																<p className="fw-bold">Accounts</p>
															</Col>
															{getPermissionsByCategory("account").map(
																(permission) => (
																	<Col
																		md={2}
																		className="mt-1 text-center"
																		key={permission.id}
																	>
																		<span>
																			{permission.permission_name.split("-")[0]}
																		</span>
																		<Form.Check
																			type="checkbox"
																			name="permissions"
																			value={String(permission.id)}
																			checked={values.permissions.includes(
																				String(permission.id),
																			)}
																			onChange={(e) => {
																				if (e.target.checked) {
																					arrayHelpers.push(
																						String(permission.id),
																					);
																				} else {
																					const idx =
																						values.permissions.indexOf(
																							String(permission.id),
																						);
																					arrayHelpers.remove(idx);
																				}
																			}}
																		/>
																	</Col>
																),
															)}
														</Row>

													</div>
												)}
											/>
										</Col>
									</Row>
									<Modal.Footer className="mt-5">
										<Row>
											<Col md={6}>
												<div className="mt-1">
													<Button onClick={closeModal} className="cancel-btn">
														<span className="text-center">Cancel</span>
													</Button>
												</div>
											</Col>
											<Col md={6}>
												<div className="mt-1">
													<Button
														type="submit"
														className="border-0 create-btn bg-color-1"
													>
														{isSubmitting ? (
															<Spinner animation="border" size="sm" />
														) : (
															<span className="text-center">Edit role</span>
														)}
													</Button>
												</div>
											</Col>
										</Row>
									</Modal.Footer>
								</Modal.Body>
							</Form>
						)}
					</Formik>
				</div>
			</Modal>
		</Container>
	);
};

export default EditRole;
